import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { moneyTick, CATEGORY_COLORS } from "@/lib/charts";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { StatCard } from "@/components/common/StatCard";
import { ChartCard } from "@/components/common/ChartCard";
import { QboInvoicesPanel } from "@/features/finance/QboInvoicesPanel";

export const BillingTab = () => {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState([]);

  const { data: pending } = useQuery({
    queryKey: ["billing-pending"],
    queryFn: async () => (await api.get("/finance/billing/pending")).data,
  });
  const { data: aging } = useQuery({
    queryKey: ["billing-aging"],
    queryFn: async () => (await api.get("/finance/billing/aging")).data,
  });
  const { data: dias } = useQuery({
    queryKey: ["billing-dias"],
    queryFn: async () => (await api.get("/finance/billing/dias-facturacion")).data,
  });

  const bulk = useMutation({
    mutationFn: (payload) => api.patch("/finance/billing/bulk", payload),
    onSuccess: ({ data }) => {
      toast.success(t("finance.billing.bulk_updated", { n: data.actualizadas }));
      setSelected([]);
      qc.invalidateQueries({ queryKey: ["billing-pending"] });
      qc.invalidateQueries({ queryKey: ["billing-aging"] });
    },
  });

  const items = pending?.items || [];
  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <div data-testid="finance-billing-tab" className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard testId="billing-total-pending" label={t("finance.billing.total_pending")} value={formatMoney(pending?.total_cents)} />
        <StatCard testId="billing-count-pending" label="Órdenes" value={pending?.cantidad ?? "—"} />
        <StatCard testId="billing-avg-days" label={t("finance.billing.avg_days")} value={dias ? `${dias.promedio_dias} d` : "—"} />
      </div>

      <div className="rounded-[10px] border border-line bg-white">
        <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
          <p className="text-[13px] font-semibold text-ink">{t("finance.billing.pending_title")}</p>
          {can("finance", "update") && selected.length > 0 && (
            <div className="flex gap-2">
              <Button data-testid="billing-mark-not-billable" size="sm" variant="outline"
                onClick={() => bulk.mutate({ ids: selected, billing_status: "no_facturable" })}>
                {t("finance.billing.mark_not_billable")}
              </Button>
            </div>
          )}
        </div>
        {!items.length ? (
          <p className="p-6 text-center text-[13px] text-ink-tertiary">{t("finance.billing.empty")}</p>
        ) : (
          <table className="w-full text-[12.5px]" data-testid="billing-pending-table">
            <thead className="bg-surface-sunken text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
              <tr>
                <th className="w-8 px-3 py-2">
                  <Checkbox data-testid="billing-select-all" checked={allSelected}
                    onCheckedChange={(v) => setSelected(v ? items.map((i) => i.work_order_id) : [])} />
                </th>
                <th className="px-3 py-2 text-left">Orden</th>
                <th className="px-3 py-2 text-left">Edificio</th>
                <th className="px-3 py-2 text-left">Monto</th>
                <th className="px-3 py-2 text-left">{t("finance.billing.days_closed")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.work_order_id} className="border-b border-line" data-testid={`billing-row-${i.work_order_id}`}>
                  <td className="px-3 py-1.5">
                    <Checkbox checked={selected.includes(i.work_order_id)}
                      onCheckedChange={(v) => setSelected((s) => v ? [...s, i.work_order_id] : s.filter((x) => x !== i.work_order_id))} />
                  </td>
                  <td className="px-3 py-1.5">{i.code}</td>
                  <td className="px-3 py-1.5">{i.property_nombre}</td>
                  <td className="px-3 py-1.5 tabular-nums">{formatMoney(i.client_price_cents)}</td>
                  <td className="px-3 py-1.5">{i.antiguedad_dias} d</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <QboInvoicesPanel />

      <div className="grid gap-4 xl:grid-cols-2">
        <ChartCard testId="billing-aging-chart" title={t("finance.billing.aging_title")} height={220} tableView={
          <table className="w-full text-[12px]"><tbody>
            {(aging?.tramos || []).map((tr) => (
              <tr key={tr.tramo}><td className="px-2 py-1">{tr.tramo}</td><td className="px-2 py-1 text-right">{formatMoney(tr.monto_cents)}</td></tr>
            ))}
          </tbody></table>
        }>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={aging?.tramos || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="tramo" tick={{ fontSize: 11, fill: "var(--ink-secondary)" }} />
              <YAxis tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Bar isAnimationActive={false} dataKey="monto_cents" fill={CATEGORY_COLORS[0]} radius={4} barSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="billing-days-chart" title={t("finance.billing.avg_days_title")} tableView={
          <table className="w-full text-[12px]"><tbody>
            {(dias?.evolucion_mensual || []).map((m) => (
              <tr key={m.mes}><td className="px-2 py-1">{m.mes}</td><td className="px-2 py-1 text-right">{m.promedio_dias} d</td></tr>
            ))}
          </tbody></table>
        }>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dias?.evolucion_mensual || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
              <YAxis tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
              <Tooltip formatter={(v) => `${v} d`} />
              <Line isAnimationActive={false} type="monotone" dataKey="promedio_dias" stroke={CATEGORY_COLORS[6]} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};
